import { Card, createStyles, Group, Stack } from '@mantine/core';
import type { LiteUserOut } from '@repo/api-codegen';
import type { UserOut as IUser } from '@repo/api-codegen/v2';
import { UserAvatar } from '@repo/common/components';
import { IconWrapper } from '@repo/common/components/IconWrapper';
import type { AvatarUser } from '@repo/common/components/UserAvatar/common';
import type { IUserGroup } from '@repo/common/models/userGroup';
import type { IconNames } from '@repo/foundations';
import {
	Icon,
	MultiSelect,
	Switch,
	Text,
	TextArea,
	TextInput,
} from '@repo/foundations';
import { useCallback, useState } from 'react';
import type { ISlackChannel } from '../../api';
import { useAnnouncementGlobalRecipientsList } from '../../components/Announcement/AnnouncementGlobalRecipients.helpers';
import { useDebouncedCallbackWithFlush } from '../../hooks/useDebouncedCallbackWithFlush';
import { BlockHeader } from '../BlockHeader/BlockHeader';
import type { NotificationSettings } from './types';
import { NotificationType } from './types';

interface NotificationBlockProps {
	includeTitle?: boolean;
	onNotificationDelete: () => void;
	notificationType: NotificationType;
	iconName: IconNames;
	toolTip: string;
	title: string;
	notificationSettings: NotificationSettings;
	onNotificationSettingsChange: (
		notificationSettings: NotificationSettings
	) => void;
}

const useStyles = createStyles((theme) => ({
	switchGroup: {
		display: 'flex',
		justifyContent: 'space-between',
	},
	group: {
		gap: theme.spacing.sm,
		padding: 0,
		width: '100%',
	},
	selectGroup: {
		gap: theme.spacing.md,
	},
	card: {
		overflow: 'visible',
	},
}));

interface NotificationData {
	label: string;
	value: string;
	group: string;
	type: string;
	original: IUser | LiteUserOut | IUserGroup | ISlackChannel;
}

export function NotificationBlock({
	onNotificationDelete,
	includeTitle = true,
	notificationType,
	iconName,
	toolTip,
	title,
	notificationSettings,
	onNotificationSettingsChange,
}: NotificationBlockProps) {
	const { classes } = useStyles();
	const { options } = useAnnouncementGlobalRecipientsList();

	const [subject, setSubject] = useState(notificationSettings.subject ?? '');
	const [message, setMessage] = useState(notificationSettings.message ?? '');

	const debouncedSubjectChange = useDebouncedCallbackWithFlush(
		(newSubject: string) => {
			const newSettings = {
				...notificationSettings,
				subject: newSubject,
			};
			onNotificationSettingsChange(newSettings);
		},
		200
	);

	const debouncedMessageChange = useDebouncedCallbackWithFlush(
		(newMessage: string) => {
			const newSettings = {
				...notificationSettings,
				message: newMessage,
			};
			onNotificationSettingsChange(newSettings);
		},
		200
	);

	const handleSubjectChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newSubject = e.currentTarget.value;
			setSubject(newSubject);

			debouncedSubjectChange(newSubject);
		},
		[debouncedSubjectChange]
	);

	const handleMessageChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const newMessage = e.currentTarget.value;
			setMessage(newMessage);

			debouncedMessageChange(newMessage);
		},
		[debouncedMessageChange]
	);

	const channelOptions = options.filter((option) => option.type === 'channel');
	const recipientOptions = options.filter(
		(option) => option.type !== 'channel'
	);

	const handleChannelChange = useCallback(
		(channels: string[]) => {
			const newChannels = channels.map((channel) => ({
				id: channel,
			}));

			const newNotificationSettings = {
				...notificationSettings,
				channels: newChannels,
			};
			onNotificationSettingsChange(newNotificationSettings);
		},
		[notificationSettings, onNotificationSettingsChange]
	);

	const handleEmailRecipientsChange = useCallback(
		(recipients: string[]) => {
			const newNotificationSettings = {
				...notificationSettings,
				users: recipientOptions
					.filter(
						(option) =>
							recipients.includes(option.value) && option.type === 'user'
					)
					.map((option) => ({
						id: option.value,
						label: option.label,
						type: option.type,
						group: option.group,
					})),
				user_groups: recipientOptions
					.filter(
						(option) =>
							recipients.includes(option.value) && option.type === 'group'
					)
					.map((option) => ({
						id: option.value,
						label: option.label,
						type: option.type,
						group: option.group,
					})),
			};
			onNotificationSettingsChange(newNotificationSettings);
		},
		[notificationSettings, onNotificationSettingsChange, recipientOptions]
	);

	const handleAnnouncementRecipientsChange = useCallback(
		(recipients: string[]) => {
			const newNotificationSettings = {
				...notificationSettings,
				users: recipientOptions
					.filter(
						(option) =>
							recipients.includes(option.value) && option.type === 'user'
					)
					.map((option) => ({
						id: option.value,
						label: option.label,
						type: option.type,
						group: option.group,
					})),
				user_groups: recipientOptions
					.filter(
						(option) =>
							recipients.includes(option.value) && option.type === 'group'
					)
					.map((option) => ({
						id: option.value,
						label: option.label,
						type: option.type,
						group: option.group,
					})),
			};
			onNotificationSettingsChange(newNotificationSettings);
		},
		[notificationSettings, onNotificationSettingsChange, recipientOptions]
	);

	const handleIncludeSummaryChange = useCallback(
		(includeSummary: boolean) => {
			const newSettings = { ...notificationSettings, includeSummary };
			onNotificationSettingsChange(newSettings);
		},
		[notificationSettings, onNotificationSettingsChange]
	);

	const renderChannelIcon = () => <Icon name="hash" size="md" />;

	const displayIcon = (item: NotificationData) => {
		if (item.type === 'user') {
			return <UserAvatar user={item.original as AvatarUser} size="xxs" />;
		} else if (item.type === 'group' && 'icon' in item.original) {
			return (
				<IconWrapper>
					<Text size="sm">{item.original.icon}</Text>
				</IconWrapper>
			);
		}
		return (
			<IconWrapper>
				<Icon name="user" />
			</IconWrapper>
		);
	};

	return (
		<Card withBorder radius="md" pt={0} className={classes.card}>
			{includeTitle && (
				<BlockHeader
					onDelete={onNotificationDelete}
					iconName={iconName}
					toolTip={toolTip}
					title={title}
					backgroundColor="fill/transparent-secondary/default"
				/>
			)}
			<Card.Section p="md">
				<Stack className={classes.selectGroup}>
					{notificationType === NotificationType.SLACK && (
						<>
							<MultiSelect
								label="Channel"
								value={
									notificationSettings.channels?.map((channel) => channel.id) ??
									[]
								}
								defaultValue={
									notificationSettings.channels?.map((channel) => channel.id) ??
									[]
								}
								setValue={handleChannelChange}
								data={channelOptions}
								renderIcon={renderChannelIcon}
								placeholder="Select"
							/>
							<TextArea
								label="Message"
								value={message}
								onChange={handleMessageChange}
							/>
						</>
					)}
					{[NotificationType.EMAIL, NotificationType.ANNOUNCEMENT].includes(
						notificationType
					) && (
						<>
							<MultiSelect
								label="Recipients"
								value={notificationSettings.recipients ?? []}
								defaultValue={notificationSettings.recipients ?? undefined}
								setValue={
									notificationType === NotificationType.EMAIL
										? handleEmailRecipientsChange
										: handleAnnouncementRecipientsChange
								}
								data={recipientOptions}
								renderIcon={displayIcon}
								placeholder="Select"
							/>
							<TextInput
								label="Subject"
								value={subject}
								onChange={handleSubjectChange}
							/>
							<TextArea
								label="Body"
								value={message}
								onChange={handleMessageChange}
							/>
						</>
					)}
				</Stack>
			</Card.Section>
			<Card.Section p="md" withBorder>
				<Group className={classes.switchGroup}>
					<Text size="sm">Include summary of automation edits</Text>
					<Switch
						checked={notificationSettings.includeSummary}
						onChange={(event) =>
							handleIncludeSummaryChange(event.currentTarget.checked)
						}
					/>
				</Group>
			</Card.Section>
		</Card>
	);
}
